import { useNavigate } from 'react-router-dom'

import { Wizard, WizardStep } from '@factorialco/deprecated-design-system'
import {
  AccountsListPageDocument,
  BankingBankAccountFragment,
  FinishBankAccountDocument,
} from 'generated/resources/graphql'

import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { getDefaultAlias } from 'modules/banking/utils'

import { WizardData } from './types'
import { FinishSetupAliasWizardStep } from './wizardSteps/FinishSetupAliasWizardStep'
import { FinishSetupLedgerAccountWizardStep } from './wizardSteps/FinishSetupLedgerAccountWizardStep'
import { FinishSetupMembershipsWizardStep } from './wizardSteps/FinishSetupMembershipsWizardStep'

interface NewAccountModalProps {
  companyId: number
  bankAccount: BankingBankAccountFragment
  legalEntityId: number
  onClose: () => void
  onFinish: () => void
}

export const FininshSetupAccountModal = ({
  onClose,
  onFinish,
  companyId,
  bankAccount,
  legalEntityId,
}: NewAccountModalProps) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const handleClose = (id: number) => {
    navigate(navigation.finance.banking.accounts.accountId.fullPath({ id }))
  }

  const [finishSetup] = useMutation(FinishBankAccountDocument, {
    extractorFn: (data) => data.bankingMutations.finishSetupBankingBankAccount,
    refetchQueries: [AccountsListPageDocument],
  })

  const finishSetupAliasWizardStep: WizardStep<WizardData> = useUnsafeMemo(
    () => ({
      title: i18n.t('banking.accounts.finish_wizard.step_1.side_title'),
      isCompleted: () => true,
      nextText: i18n.t('banking.accounts.finish_wizard.next'),
      render: (props) => <FinishSetupAliasWizardStep {...props} bankAccount={bankAccount} />,
    }),
    [bankAccount]
  )

  const finishSetupLedgerAccountWizardStep: WizardStep<WizardData> = useUnsafeMemo(
    () => ({
      title: i18n.t('banking.accounts.finish_wizard.step_2.side_title'),
      isCompleted: () => true,
      nextText: i18n.t('banking.accounts.finish_wizard.next'),
      render: (props) => (
        <FinishSetupLedgerAccountWizardStep
          {...props}
          bankAccount={bankAccount}
          legalEntityId={legalEntityId}
        />
      ),
    }),
    [bankAccount, legalEntityId]
  )

  const finishSetupMembershipsWizardStep: WizardStep<WizardData> = useUnsafeMemo(() => ({
    title: i18n.t('banking.accounts.finish_wizard.step_3.side_title'),
    isCompleted: () => true,
    nextText: i18n.t('banking.accounts.finish_wizard.finish_account'),
    render: (props) => <FinishSetupMembershipsWizardStep {...props} />,
  }))

  const steps = useUnsafeMemo(() => {
    return [
      finishSetupAliasWizardStep,
      finishSetupLedgerAccountWizardStep,
      finishSetupMembershipsWizardStep,
    ].filter((n) => n)
  }, [
    finishSetupAliasWizardStep,
    finishSetupLedgerAccountWizardStep,
    finishSetupMembershipsWizardStep,
  ])

  const initialData: WizardData = useUnsafeMemo(
    () => ({
      ledgerAccountId: undefined,
      accountAlias: bankAccount.accountAlias ?? getDefaultAlias(),
    }),
    [bankAccount.accountAlias]
  )

  const handleSubmit = async (data: WizardData) => {
    const result = await finishSetup({
      variables: {
        bankAccountId: bankAccount.id,
        companyId,
        ledgerAccountId: data.ledgerAccountId,
        accountAlias: data.accountAlias,
        bankAccountMembershipEmployeeIds: data.membershipsEmployeeIds,
      },
    }).then((res) => {
      if (data.membershipsEmployeeIds && data.membershipsEmployeeIds.length > 0)
        track(trackingEvents.accounting.banking.addPeopleBankAccount)
      return res
    })

    if (result) {
      handleClose(bankAccount.id)
      onFinish()
    }
  }

  return (
    <>
      <Wizard.Modal
        title={i18n.t('banking.accounts.finish_wizard.title')}
        icon="add-block"
        description={i18n.t('banking.accounts.finish_wizard.description')}
        initialData={initialData}
        onSubmit={handleSubmit}
        onClose={onClose}
        steps={steps as WizardStep<WizardData>[]}
      />
    </>
  )
}
